import { supabase } from '@/integrations/supabase/client';
import { startOfDay, startOfWeek, format } from 'date-fns';
import type { Achievement, UserMetrics } from './types';

function calculateCorrectStreak(
  answers: { is_correct: boolean; answered_at: string }[],
): number {
  if (!answers.length) return 0;
  const sorted = [...answers].sort(
    (a, b) => new Date(b.answered_at).getTime() - new Date(a.answered_at).getTime(),
  );
  let streak = 0;
  for (const answer of sorted) {
    if (answer.is_correct) streak++;
    else break;
  }
  return streak;
}

export async function fetchUserMetrics(userId: string): Promise<UserMetrics> {
  const today = format(startOfDay(new Date()), 'yyyy-MM-dd');
  const weekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const ENEM_EXAM_TYPE_ID = 'f57ab166-ae14-4e6e-a443-d7966a686430';

  const [
    answersRes, streakRes, simulationsRes, pomodoroTotalRes, pomodoroTodayRes,
    pomodoroWeekRes, goalStreaksRes, enemSimsRes, enemAnswersRes, enemProgressRes,
  ] = await Promise.all([
    supabase.from('user_answers').select('is_correct, answered_at').eq('user_id', userId).eq('module_type', 'concursos').eq('is_official', true),
    supabase.from('user_streaks').select('current_streak').eq('user_id', userId).eq('module_type', 'concursos').maybeSingle(),
    supabase.from('simulation_attempts').select('score, status').eq('user_id', userId).eq('status', 'completed').eq('is_official', true),
    supabase.from('pomodoro_sessions').select('pomodoros_completed, questions_answered').eq('user_id', userId),
    supabase.from('pomodoro_sessions').select('pomodoros_completed').eq('user_id', userId).eq('session_date', today),
    supabase.from('pomodoro_sessions').select('pomodoros_completed').eq('user_id', userId).gte('session_date', weekStart),
    supabase.from('user_goal_streaks').select('goal_type, current_streak').eq('user_id', userId),
    supabase.from('simulation_attempts').select('score, time_spent_seconds, simulations!inner(exam_type_id, time_limit_minutes)').eq('user_id', userId).eq('simulations.exam_type_id', ENEM_EXAM_TYPE_ID).eq('status', 'completed').eq('is_official', true),
    supabase.from('user_answers').select('is_correct, answered_at, questions!inner(exam_type_id, discipline:disciplines(name))').eq('user_id', userId).eq('questions.exam_type_id', ENEM_EXAM_TYPE_ID).order('answered_at', { ascending: false }).eq('is_official', true),
    supabase.from('user_enem_progress').select('enem_xp, enem_level').eq('user_id', userId).maybeSingle(),
  ]);

  const answers = answersRes.data || [];
  const simulations = simulationsRes.data || [];
  const pomodoroSessions = pomodoroTotalRes.data || [];
  const pomodoroToday = pomodoroTodayRes.data || [];
  const pomodoroWeek = pomodoroWeekRes.data || [];
  const goalStreaks = goalStreaksRes.data || [];
  const enemSims = enemSimsRes.data || [];
  const enemAnswers = enemAnswersRes.data || [];
  const enemProgress = enemProgressRes.data;

  const getGoalStreak = (type: string) =>
    goalStreaks.find((s) => s.goal_type === type)?.current_streak || 0;

  const enemTriScores = enemSims.map((s) => Math.round(450 + ((s.score || 0) / 100) * 500));

  let enemFastestUsedPercentage: number | null = null;
  if (enemSims.length > 0) {
    const percentages = enemSims.map((s) => {
      const limit = (s.simulations as any)?.time_limit_minutes || 60;
      return ((s.time_spent_seconds || 0) / (limit * 60)) * 100;
    });
    enemFastestUsedPercentage = Math.min(...percentages);
  }

  let enemCorrectStreak = 0;
  for (const answer of enemAnswers) {
    if (answer.is_correct) enemCorrectStreak++;
    else break;
  }

  const areaMap: Record<string, string> = {
    'língua portuguesa': 'linguagens', 'português': 'linguagens', 'literatura': 'linguagens',
    'matemática': 'matematica',
    'física': 'natureza', 'química': 'natureza', 'biologia': 'natureza',
    'história': 'humanas', 'geografia': 'humanas', 'filosofia': 'humanas', 'sociologia': 'humanas',
  };

  const enemAreaAccuracy: Record<string, { correct: number; total: number }> = {
    linguagens: { correct: 0, total: 0 },
    matematica: { correct: 0, total: 0 },
    natureza: { correct: 0, total: 0 },
    humanas: { correct: 0, total: 0 },
  };

  enemAnswers.forEach((answer) => {
    const disciplineName = ((answer.questions as any)?.discipline?.name || '').toLowerCase();
    const area = areaMap[disciplineName];
    if (area && enemAreaAccuracy[area]) {
      enemAreaAccuracy[area].total++;
      if (answer.is_correct) enemAreaAccuracy[area].correct++;
    }
  });

  return {
    totalAnswers: answers.length,
    correctAnswers: answers.filter((a) => a.is_correct).length,
    currentStreak: streakRes.data?.current_streak || 0,
    simulationsCompleted: simulations.length,
    perfectSimulations: simulations.filter((s) => s.score === 100).length,
    correctStreak: calculateCorrectStreak(answers),
    pomodorosTotal: pomodoroSessions.reduce((sum, s) => sum + (s.pomodoros_completed || 0), 0),
    pomodorosToday: pomodoroToday.reduce((sum, s) => sum + (s.pomodoros_completed || 0), 0),
    pomodorosThisWeek: pomodoroWeek.reduce((sum, s) => sum + (s.pomodoros_completed || 0), 0),
    pomodoroQuestions: pomodoroSessions.reduce((sum, s) => sum + (s.questions_answered || 0), 0),
    questionsGoalStreak: getGoalStreak('questions'),
    timeGoalStreak: getGoalStreak('study_time'),
    pomodoroGoalStreak: getGoalStreak('pomodoros'),
    enemLevel: enemProgress?.enem_level || 1,
    enemXp: enemProgress?.enem_xp || 0,
    enemTriScores,
    enemSimulationsCompleted: enemSims.length,
    enemQuestionsAnswered: enemAnswers.length,
    enemCorrectStreak,
    enemAreaAccuracy,
    enemFastestUsedPercentage,
  };
}

export function meetsRequirement(achievement: Achievement, metrics: UserMetrics): boolean {
  switch (achievement.requirement_type) {
    case 'correct_answers': return metrics.correctAnswers >= achievement.requirement_value;
    case 'total_answers': return metrics.totalAnswers >= achievement.requirement_value;
    case 'daily_streak': return metrics.currentStreak >= achievement.requirement_value;
    case 'simulations_completed': return metrics.simulationsCompleted >= achievement.requirement_value;
    case 'perfect_simulation': return metrics.perfectSimulations >= achievement.requirement_value;
    case 'correct_streak': return metrics.correctStreak >= achievement.requirement_value;
    case 'pomodoros_total': return metrics.pomodorosTotal >= achievement.requirement_value;
    case 'pomodoros_daily': return metrics.pomodorosToday >= achievement.requirement_value;
    case 'pomodoros_weekly': return metrics.pomodorosThisWeek >= achievement.requirement_value;
    case 'pomodoro_questions': return metrics.pomodoroQuestions >= achievement.requirement_value;
    case 'questions_goal_streak': return metrics.questionsGoalStreak >= achievement.requirement_value;
    case 'time_goal_streak': return metrics.timeGoalStreak >= achievement.requirement_value;
    case 'pomodoro_goal_streak': return metrics.pomodoroGoalStreak >= achievement.requirement_value;
    case 'enem_level': return metrics.enemLevel >= achievement.requirement_value;
    case 'enem_xp': return metrics.enemXp >= achievement.requirement_value;
    case 'enem_tri_900': return metrics.enemTriScores.some((s) => s >= 900);
    case 'enem_tri_800': return metrics.enemTriScores.some((s) => s >= 800);
    case 'enem_tri_700': return metrics.enemTriScores.some((s) => s >= 700);
    case 'enem_first_simulation': return metrics.enemSimulationsCompleted >= 1;
    case 'enem_simulations_completed': return metrics.enemSimulationsCompleted >= achievement.requirement_value;
    case 'enem_questions_answered': return metrics.enemQuestionsAnswered >= achievement.requirement_value;
    case 'enem_math_mastery': {
      const m = metrics.enemAreaAccuracy.matematica;
      return m.total >= 20 && (m.correct / m.total) * 100 >= achievement.requirement_value;
    }
    case 'enem_humanas_mastery': {
      const h = metrics.enemAreaAccuracy.humanas;
      return h.total >= 20 && (h.correct / h.total) * 100 >= achievement.requirement_value;
    }
    case 'enem_natureza_mastery': {
      const n = metrics.enemAreaAccuracy.natureza;
      return n.total >= 20 && (n.correct / n.total) * 100 >= achievement.requirement_value;
    }
    case 'enem_linguagens_mastery': {
      const l = metrics.enemAreaAccuracy.linguagens;
      return l.total >= 20 && (l.correct / l.total) * 100 >= achievement.requirement_value;
    }
    case 'enem_speed_finish':
      return metrics.enemFastestUsedPercentage !== null && metrics.enemFastestUsedPercentage <= achievement.requirement_value;
    case 'enem_correct_streak':
      return metrics.enemCorrectStreak >= achievement.requirement_value;
    default:
      return false;
  }
}

/**
 * Lê (read-only) catálogo de conquistas e quais o usuário já desbloqueou.
 * Útil para componentes que listam conquistas.
 */
export async function fetchAchievementsList(userId: string) {
  const [achievementsRes, userAchievementsRes] = await Promise.all([
    supabase.from('achievements').select('*'),
    supabase.from('user_achievements').select('achievement_id').eq('user_id', userId),
  ]);
  const all = (achievementsRes.data || []) as Achievement[];
  const unlockedIds = new Set(userAchievementsRes.data?.map((ua) => ua.achievement_id) || []);
  return {
    all,
    unlockedIds,
    notUnlocked: all.filter((a) => !unlockedIds.has(a.id)),
  };
}
